import { useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import type { TokenState } from "../shared/messages";
import type { DiceRollPayload } from "../shared/diceRoll";
import {
  extractCreatureExperiences,
  isDaggerheartAdversary,
  isDaggerheartPC,
  newDiceRollId,
  parseCreatureFromNoteText,
  type CreatureExperience,
} from "../shared/diceRoll";
import { pickFsbPcHtml, resolveTraitFromText } from "../shared/fsbPcSheet";
import {
  applyRestMoves,
  REST_MOVE_COUNT,
  REST_MOVES,
  type RestKind,
  type RestMoveId,
} from "../shared/pcGear";
import { resolvePcHopeCurrent, resolvePcHopeMax } from "../shared/pcHope";
import { DualityOptionsModal } from "./PathADiceTray";

function num(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = parseInt(value.replace(/[^0-9-]/g, ""), 10);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
}

export function buildDualityRoll(
  token: TokenState,
  traitName: string,
  modifier: number,
  opts: {
    advantage?: boolean;
    disadvantage?: boolean;
    experience?: CreatureExperience | null;
  },
): DiceRollPayload {
  const expBonus = opts.experience?.modifier ?? 0;
  let formula = `2d12${modifier === 0 ? "" : modifier > 0 ? `+${modifier}` : `${modifier}`}`;
  if (expBonus !== 0) formula += expBonus > 0 ? `+${expBonus}` : `${expBonus}`;
  if (opts.advantage) formula += "+1d6";
  if (opts.disadvantage) formula += "-1d6";
  return {
    id: newDiceRollId(),
    tokenId: token.id,
    tokenName: token.name,
    abilityName: traitName,
    formula,
    faces: [
      { label: "Hope", value: 0, sides: 12 },
      { label: "Fear", value: 0, sides: 12 },
      ...(opts.advantage ? [{ label: "Adv", value: 0, sides: 6 }] : []),
      ...(opts.disadvantage ? [{ label: "Dis", value: 0, sides: 6 }] : []),
    ],
    modifier,
    total: 0,
    extraModLabel: opts.experience
      ? `${opts.experience.name} ${expBonus >= 0 ? `+${expBonus}` : String(expBonus)}`
      : null,
    pending: true,
    source: "board",
    at: Date.now(),
  };
}

export function buildFormulaRoll(
  token: TokenState,
  abilityName: string,
  formula: string,
  fallbackModifier = 0,
  _baseDieSides = 20,
): DiceRollPayload {
  return {
    id: newDiceRollId(),
    tokenId: token.id,
    tokenName: token.name,
    abilityName,
    formula,
    faces: [],
    modifier: fallbackModifier,
    total: 0,
    pending: true,
    source: "board",
    at: Date.now(),
  };
}

function PropRow(props: { label: string; value: string }) {
  if (!props.value) return null;
  return (
    <div className="path-a-dh-prop">
      <span className="path-a-dh-prop__name">{props.label}</span>
      <span className="path-a-dh-prop__value">{props.value}</span>
    </div>
  );
}

function TrackerBoxes(props: {
  label: string;
  max: number;
  marked: number;
  disabled?: boolean;
  onChange: (nextMarked: number) => void;
}) {
  const { label, max, marked, disabled, onChange } = props;
  if (max <= 0) return null;
  return (
    <div className="dh-board-tracker-row">
      <span className="dh-board-tracker-label">{label}</span>
      <div className="dh-board-boxes">
        {Array.from({ length: max }, (_, i) => {
          const isMarked = i < marked;
          return (
            <input
              key={i}
              type="checkbox"
              className="dh-board-box"
              checked={isMarked}
              disabled={disabled}
              title={`${label} slot ${i + 1}`}
              onChange={() => onChange(isMarked ? i : i + 1)}
            />
          );
        })}
      </div>
    </div>
  );
}

function RestMovesModal(props: {
  kind: RestKind;
  selected: RestMoveId[];
  onToggle: (id: RestMoveId) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const need = REST_MOVE_COUNT[props.kind];
  const title = props.kind === "short" ? "Short Rest" : "Long Rest";
  return (
    <div className="dh-duality-modal-backdrop" onClick={props.onCancel}>
      <div
        className="dh-duality-modal"
        role="dialog"
        aria-label={`${title} moves`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dh-duality-modal-header">
          <h4>
            {title}: choose {need} downtime moves
          </h4>
          <button type="button" className="path-a-statblock-close-btn" onClick={props.onCancel}>
            ×
          </button>
        </div>
        <div className="dh-duality-modal-body">
          <div className="dh-duality-section">
            <div className="dh-duality-section-title">Daggerheart rest moves</div>
            <div className="dh-duality-choices">
              {REST_MOVES.map((move) => (
                <label key={move.id} className="dh-duality-radio">
                  <input
                    type="checkbox"
                    checked={props.selected.includes(move.id)}
                    onChange={() => props.onToggle(move.id)}
                  />{" "}
                  {move.label} — {move.detail}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="dh-duality-modal-footer">
          <button type="button" className="dh-pc-btn" onClick={props.onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="dh-pc-btn mod-cta"
            disabled={props.selected.length !== need}
            onClick={props.onConfirm}
          >
            Take {title}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PcStatblockSheet(props: {
  token: TokenState;
  noteText: string;
  isOwner: boolean;
  role: "gm" | "player";
  ownerLabel: string;
  onClose: () => void;
  onRoll: (roll: DiceRollPayload) => void;
  onPrepareRoll?: () => void;
  onUpdateHp: (current: number) => void;
  onUpdateStress: (current: number) => void;
  onUpdateArt?: (file: File) => void;
}) {
  const {
    token,
    noteText,
    isOwner,
    role,
    ownerLabel,
    onClose,
    onRoll,
    onPrepareRoll,
    onUpdateHp,
    onUpdateStress,
    onUpdateArt,
  } = props;
  const creature = useMemo(
    () => parseCreatureFromNoteText(noteText) ?? { name: token.name },
    [noteText, token.name],
  );
  const experiences = useMemo(() => extractCreatureExperiences(creature), [creature]);
  const adversary = isDaggerheartAdversary(creature);
  const pc = isDaggerheartPC(creature) || !adversary;
  const [duality, setDuality] = useState<{ trait: string; mod: number } | null>(null);
  const [restKind, setRestKind] = useState<RestKind | null>(null);
  const [restPicks, setRestPicks] = useState<RestMoveId[]>([]);
  const [markedArmor, setMarkedArmor] = useState(num(creature.marked_armor, 0));
  const maxHope = resolvePcHopeMax(creature);
  const [hope, setHope] = useState(resolvePcHopeCurrent(creature, maxHope));

  const maxHp = token.hp?.max ?? num(creature.max_hp, num(creature.hp, 6));
  const curHp = token.hp?.current ?? Math.max(0, maxHp - num(creature.marked_hp, 0));
  const maxStress = token.stress?.max ?? num(creature.max_stress, num(creature.stress, 6));
  const curStress = token.stress?.current ?? num(creature.marked_fear, num(creature.marked_stress, 0));
  const curHope = hope;
  const maxArmor = num(creature.armor, 0);
  const atk = text(creature.atk) || "+0";
  const attack = text(creature.attack);
  const primaryWeapon = text(creature.primary_weapon);
  const secondaryWeapon = text(creature.secondary_weapon);
  const domains = text(creature.domain_cards);
  const background = text(creature.background);
  const connections = text(creature.connections);
  const concept = text(
    creature.concept ||
      creature.description ||
      creature.character_concept ||
      creature.pronouns,
  );
  const identity = [
    creature.level != null ? `Level ${text(creature.level) || creature.level}` : "",
    creature.tier != null ? `Tier ${text(creature.tier) || creature.tier}` : "",
    text(creature.class),
    text(creature.subclass),
    text(creature.ancestry),
    text(creature.community),
    adversary ? text(creature.type) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const canRoll = isOwner || role === "gm";

  useEffect(() => {
    setMarkedArmor(num(creature.marked_armor, 0));
  }, [token.id, creature.marked_armor]);

  useEffect(() => {
    setHope(resolvePcHopeCurrent(creature, maxHope));
  }, [token.id, creature.hope, creature.hope_max, creature.max_hope, maxHope]);

  const fsbHtml = useMemo(
    () => pickFsbPcHtml(token.statblockHtml, creature, token.name),
    [token.statblockHtml, creature, token.name],
  );

  const startDuality = (trait: string, mod: number) => {
    if (!canRoll) return;
    onPrepareRoll?.();
    if (pc) {
      setDuality({ trait, mod });
      return;
    }
    onRoll(buildFormulaRoll(token, trait, `1d20${mod >= 0 ? `+${mod}` : mod}`, mod, 20));
  };

  const rollFormula = (ability: string, formula: string, sides = 6) => {
    if (!canRoll) return;
    onPrepareRoll?.();
    onRoll(buildFormulaRoll(token, ability, formula, 0, sides));
  };

  const activateFsbRoll = (ev: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    if (!canRoll) return;
    if ("key" in ev && ev.key !== "Enter" && ev.key !== " ") return;
    const target = ev.target;
    if (!(target instanceof Element)) return;
    if (target.closest(".difficulty, [data-property='difficulty']")) return;
    const stamped = target.closest<HTMLElement>("[data-dh-roll]");
    const kind = stamped?.dataset.dhRoll || "";
    const payload = stamped?.dataset.dhRollPayload || "";
    const traitHit =
      kind === "dice" || kind === "exp" || kind === "atk" || kind === "attack"
        ? null
        : resolveTraitFromText((stamped ?? target).textContent || "") ||
          (kind === "attr" || kind === "duality"
            ? (() => {
                const [trait, modRaw] = payload.split("|");
                return trait
                  ? { trait, mod: parseInt(modRaw || "0", 10) || 0 }
                  : null;
              })()
            : null);
    if (!traitHit && !stamped) return;
    ev.preventDefault();
    ev.stopPropagation();
    if (traitHit) {
      startDuality(traitHit.trait, traitHit.mod);
      return;
    }
    if (!stamped) return;
    if (kind === "dice") {
      rollFormula(payload || "Damage", payload || "1d6");
      return;
    }
    if ((kind === "atk" || kind === "attack") && pc) {
      const [trait, modRaw] = payload.split("|");
      startDuality(trait || "Attack", parseInt(modRaw || "0", 10) || 0);
      return;
    }
    if (kind === "exp") {
      const m = /([+-]?\d+)/.exec(payload);
      const mod = m ? parseInt(m[1]!, 10) : 0;
      const name = payload.replace(/[+-]?\d+/, "").trim() || "Experience";
      startDuality(name, mod);
    }
  };

  return (
    <aside
      className={`sheet-panel token-sheet-panel dh-fsb-sheet path-a-dh-sheet${pc ? " path-a-dh-sheet--pc" : ""}`}
      data-testid="token-sheet-panel"
      aria-label={`Token ${token.name}`}
    >
      <div className="sheet-panel-head">
        <div className="dh-fsb-head-row">
          {token.artUrl || onUpdateArt ? (
            <label
              className={`dh-fsb-portrait-wrap${onUpdateArt ? " is-editable" : ""}`}
              title={onUpdateArt ? "Change profile art" : undefined}
            >
              {token.artUrl ? (
                <img src={token.artUrl} alt="" className="dh-fsb-portrait" />
              ) : (
                <span className="dh-fsb-portrait dh-fsb-portrait--empty">Art</span>
              )}
              {onUpdateArt ? (
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  hidden
                  onChange={(ev) => {
                    const file = ev.target.files?.[0];
                    ev.target.value = "";
                    if (file) onUpdateArt(file);
                  }}
                />
              ) : null}
            </label>
          ) : null}
          {pc ? (
            <div className="path-a-fsb-chrome-name muted">Player sheet</div>
          ) : (
            <div>
              <h3 className="dh-fsb-name">{text(creature.name) || token.name}</h3>
              {identity ? <div className="dh-fsb-meta">{identity}</div> : null}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn secondary btn-tiny"
          data-testid="token-sheet-close"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {pc ? (
        <div
          className="path-a-fsb-mount"
          data-testid="fsb-pc-mount"
          dangerouslySetInnerHTML={{ __html: fsbHtml }}
          onClick={activateFsbRoll}
          onKeyDown={activateFsbRoll}
        />
      ) : (
        <>
          <div className="path-a-dh-sheet__rule" />

          {concept ? <div className="path-a-dh-concept">{concept}</div> : null}

          <div className="path-a-dh-props">
            <PropRow
              label="Proficiency"
              value={
                creature.proficiency != null
                  ? String(text(creature.proficiency) || creature.proficiency)
                  : ""
              }
            />
            <PropRow
              label="Evasion"
              value={
                creature.evasion != null
                  ? String(text(creature.evasion) || creature.evasion)
                  : ""
              }
            />
            <PropRow
              label="Armor"
              value={
                [text(creature.armor_name), creature.armor != null ? String(text(creature.armor) || creature.armor) : ""]
                  .filter(Boolean)
                  .join(" · ")
              }
            />
            <PropRow
              label="Thresholds"
              value={creature.thresholds != null ? text(creature.thresholds) : ""}
            />
            {adversary && creature.difficulty != null ? (
              <div className="path-a-dh-prop dh-fsb-difficulty" title="Difficulty is plain text and does not roll">
                <span className="path-a-dh-prop__name">Difficulty</span>
                <span className="path-a-dh-prop__value">
                  {String(text(creature.difficulty) || creature.difficulty)}
                </span>
              </div>
            ) : null}
          </div>
        </>
      )}

      {adversary ? (
        <div className="dh-fsb-roll-row">
          <button
            type="button"
            className="dh-board-btn"
            disabled={!canRoll}
            onClick={() => {
              const atkNum = num(atk, 0);
              onPrepareRoll?.();
              onRoll(
                buildFormulaRoll(
                  token,
                  "Attack",
                  `1d20${atkNum >= 0 ? `+${atkNum}` : atkNum}`,
                  atkNum,
                  20,
                ),
              );
            }}
          >
            ATK {atk}
          </button>
          {attack ? (
            <button
              type="button"
              className="dh-board-btn"
              disabled={!canRoll}
              onClick={() => {
                const atkNum = num(atk, 0);
                onPrepareRoll?.();
                onRoll(
                  buildFormulaRoll(
                    token,
                    "Attack",
                    `1d20${atkNum >= 0 ? `+${atkNum}` : atkNum}`,
                    atkNum,
                    20,
                  ),
                );
              }}
            >
              {attack}
            </button>
          ) : null}
        </div>
      ) : null}

      {!pc && (primaryWeapon || secondaryWeapon) && (
        <div className="dh-fsb-section">
          <div className="dh-fsb-section-title">Weapons</div>
          {[primaryWeapon, secondaryWeapon].filter(Boolean).map((w) => {
            const dice = /\b(\d+)?d(\d+)(?:\s*[+-]\s*\d+)?\b/i.exec(w);
            return (
              <button
                key={w}
                type="button"
                className="dh-fsb-dice-link"
                disabled={!canRoll || !dice}
                onClick={() => dice && rollFormula("Damage", dice[0]!.replace(/\s+/g, ""))}
              >
                {w}
              </button>
            );
          })}
        </div>
      )}

      {!pc && experiences.length > 0 ? (
        <div className="dh-fsb-section">
          <div className="dh-fsb-section-title">Experience</div>
          {experiences.map((exp) => (
            <button
              key={exp.text}
              type="button"
              className="dh-fsb-dice-link"
              disabled={!canRoll}
              title={`Roll Experience (1d20 ${exp.modifier >= 0 ? "+" + exp.modifier : exp.modifier})`}
              onClick={() => {
                onPrepareRoll?.();
                onRoll(
                  buildFormulaRoll(
                    token,
                    "Experience",
                    `1d20${exp.modifier >= 0 ? `+${exp.modifier}` : exp.modifier}`,
                    exp.modifier,
                    20,
                  ),
                );
              }}
            >
              {exp.text}
            </button>
          ))}
        </div>
      ) : null}

      {!pc && domains ? (
        <div className="dh-fsb-section">
          <div className="dh-fsb-section-title">Domain cards</div>
          <div className="dh-fsb-lore">{domains}</div>
        </div>
      ) : null}

      {canRoll ? (
        <div className="dh-board-trackers">
          <TrackerBoxes
            label="HP Damage"
            max={maxHp}
            marked={Math.max(0, maxHp - curHp)}
            disabled={!isOwner && role !== "gm"}
            onChange={(next) => onUpdateHp(maxHp - next)}
          />
          {pc ? (
            <TrackerBoxes
              label="Hope"
              max={maxHope}
              marked={hope}
              disabled={!isOwner && role !== "gm"}
              onChange={setHope}
            />
          ) : null}
          <TrackerBoxes
            label="Stress"
            max={maxStress}
            marked={curStress}
            disabled={!isOwner && role !== "gm"}
            onChange={onUpdateStress}
          />
          {maxArmor > 0 ? (
            <TrackerBoxes
              label="Armor"
              max={maxArmor}
              marked={markedArmor}
              disabled={!isOwner && role !== "gm"}
              onChange={setMarkedArmor}
            />
          ) : null}
        </div>
      ) : null}

      {canRoll && pc ? (
        <div className="dh-board-actions-row">
          <button
            type="button"
            className="dh-board-btn"
            onClick={() => {
              setRestPicks([]);
              setRestKind("short");
            }}
          >
            Short Rest
          </button>
          <button
            type="button"
            className="dh-board-btn"
            onClick={() => {
              setRestPicks([]);
              setRestKind("long");
            }}
          >
            Long Rest
          </button>
          <button
            type="button"
            className="dh-board-btn mod-cta"
            onClick={() => startDuality("Duality", 0)}
          >
            Roll 2d12
          </button>
        </div>
      ) : null}

      {!pc && (background || connections) && (
        <div className="dh-fsb-section">
          {background ? (
            <>
              <div className="dh-fsb-section-title">Background</div>
              <div className="dh-fsb-lore">{background}</div>
            </>
          ) : null}
          {connections ? (
            <>
              <div className="dh-fsb-section-title">Connections</div>
              <div className="dh-fsb-lore">{connections}</div>
            </>
          ) : null}
        </div>
      )}

      <div className="path-a-dh-seat muted">{ownerLabel}</div>

      {duality
        ? createPortal(
            <DualityOptionsModal
              traitName={duality.trait}
              modifier={duality.mod}
              experiences={experiences}
              onCancel={() => setDuality(null)}
              onConfirm={(choice) => {
                try {
                  onRoll(buildDualityRoll(token, duality.trait, duality.mod, choice));
                } finally {
                  setDuality(null);
                }
              }}
            />,
            document.body,
          )
        : null}

      {restKind
        ? createPortal(
            <RestMovesModal
              kind={restKind}
              selected={restPicks}
              onToggle={(id) => {
                setRestPicks((prev) => {
                  if (prev.includes(id)) return prev.filter((item) => item !== id);
                  if (prev.length >= REST_MOVE_COUNT[restKind]) return prev;
                  return [...prev, id];
                });
              }}
              onCancel={() => {
                setRestKind(null);
                setRestPicks([]);
              }}
              onConfirm={() => {
                const next = applyRestMoves(
                  {
                    hp: curHp,
                    maxHp,
                    stress: curStress,
                    maxStress,
                    armor: markedArmor,
                    hope,
                    maxHope,
                  },
                  restPicks,
                  restKind,
                );
                onUpdateHp(next.hp);
                onUpdateStress(next.stress);
                setMarkedArmor(next.armor);
                setHope(next.hope);
                setRestKind(null);
                setRestPicks([]);
              }}
            />,
            document.body,
          )
        : null}
    </aside>
  );
}
